/**
 * Review writer: the reviews a proposal or publication received (a funder's evaluation with scores per
 * criterion and a decision, or a journal's decision with its reviewers' comments) and the team's answer to
 * each comment, which together make the response letter.
 * Backend: /proposal/review-rounds/ (paste/, letter/) and /proposal/review-points/ (proposal/proposal/reviews.py).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid2 as Grid, IconButton, LinearProgress, List, ListItemButton, ListItemText, MenuItem, Paper, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as CollapseIcon,
  DragIndicator as DragIcon,
  ChevronRight as ExpandIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentPaste as PasteIcon,
  Description as LetterIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import ProposalTodoPanel from './ProposalTodoPanel';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

const Z = 3000;                        // above the writer, like its other dialogs
const MENU = { MenuProps: { sx: { zIndex: Z + 1 } } };

const SOURCES = [
  { value: 'funder', label: 'Funder evaluation' },
  { value: 'journal', label: 'Journal or conference' },
  { value: 'internal', label: 'Internal review' },
  { value: 'other', label: 'Other' },
];
const DECISIONS = [
  { value: '', label: 'No decision yet' },
  { value: 'funded', label: 'Funded' },
  { value: 'reserve', label: 'Reserve list' },
  { value: 'not_funded', label: 'Not funded' },
  { value: 'accept', label: 'Accept' },
  { value: 'minor', label: 'Accept with minor revisions' },
  { value: 'major', label: 'Accept with major revisions' },
  { value: 'resubmit', label: 'Reject and resubmit' },
  { value: 'reject', label: 'Reject' },
];
// What each reviewer recommends to the editor (the round's decision above is the editor's)
const RECOMMENDATIONS = [
  { value: 'accept', label: 'Accept' },
  { value: 'minor', label: 'Accept with minor revisions' },
  { value: 'major', label: 'Accept with major revisions' },
  { value: 'reject', label: 'Reject' },
];
const KINDS = [
  { value: 'major', label: 'Major' },
  { value: 'minor', label: 'Minor' },
  { value: 'other', label: 'Other' },
];
const STATUSES = [
  { value: 'open', label: 'To do', color: 'default' },
  { value: 'drafted', label: 'Answer drafted', color: 'info' },
  { value: 'addressed', label: 'Addressed', color: 'success' },
  { value: 'rebutted', label: 'Respectfully disagreed', color: 'warning' },
];
const DECISION_COLOR = { funded: 'success', accept: 'success', minor: 'info', major: 'warning', reserve: 'info',
  resubmit: 'warning', not_funded: 'error', reject: 'error' };
// The usual criteria, as a start for a funder's evaluation
const FUNDER_CRITERIA = [
  { name: 'Excellence', score: '', max: 5, threshold: 3 },
  { name: 'Impact', score: '', max: 5, threshold: 3 },
  { name: 'Quality and efficiency of the implementation', score: '', max: 5, threshold: 3 },
];

// The sidebar's layout, remembered per browser: how much of its height the reviewers take, and collapsed or not
const LAYOUT_KEY = 'otable.reviews.sidebar';
const readLayout = () => {
  try { return { split: 50, collapsed: false, ...JSON.parse(localStorage.getItem(LAYOUT_KEY) || '{}') }; }
  catch { return { split: 50, collapsed: false }; }
};
const saveLayout = (layout) => { try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout)); } catch { /* ignore */ } };

const label = (list, value) => list.find((o) => o.value === value)?.label || value;
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// ── the round's details: create or edit ──────────────────────────────────────
// A manuscript goes to one journal at a time, so its reviews are named after the journal and the round:
// "Nature Microbiology (round 1)", then the reviews of the revision "Nature Microbiology (round 2)"
function journalTitle(journal, existingTitles) {
  const same = existingTitles.filter((t) => t === journal || t.startsWith(`${journal} (round `)).length;
  return `${journal} (round ${same + 1})`;
}

function RoundForm({ open, onClose, round, proposalId, isPublication, targetJournal, existingTitles = [], onSaved }) {
  const editing = Boolean(round?.id);
  const journal = isPublication ? (targetJournal || '').trim() : '';
  const blank = {
    title: journal ? journalTitle(journal, existingTitles) : isPublication ? 'Round 1' : 'Evaluation Summary Report',
    source: isPublication ? 'journal' : 'funder',
    received_on: '', response_due: '', decision: '', overall_score: '', max_score: '', threshold: '',
    criteria: isPublication ? [] : FUNDER_CRITERIA, summary: '',
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(editing ? {
      ...blank, ...round,
      received_on: round.received_on || '', response_due: round.response_due || '',
      overall_score: round.overall_score ?? '', max_score: round.max_score ?? '', threshold: round.threshold ?? '',
      criteria: round.criteria || [],
    } : blank);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCriterion = (i, k, v) => setForm((f) => ({ ...f, criteria: f.criteria.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const num = (v) => (v === '' || v === null || v === undefined ? null : v);

  const save = async () => {
    if (!form.title.trim()) { setError('Give these reviews a title.'); return; }
    setBusy(true);
    setError(null);
    const body = {
      title: form.title, source: form.source, decision: form.decision, summary: form.summary,
      received_on: form.received_on || null, response_due: form.response_due || null,
      overall_score: num(form.overall_score), max_score: num(form.max_score), threshold: num(form.threshold),
      criteria: form.criteria.filter((c) => String(c.name || '').trim()).map((c) => ({
        name: c.name, score: num(c.score), max: num(c.max), threshold: num(c.threshold), comment: c.comment || '',
      })),
    };
    try {
      const { data } = editing
        ? await axiosService.patch(`/proposal/review-rounds/${round.id}/`, body)
        : await axiosService.post('/proposal/review-rounds/', { ...body, proposal: proposalId, reviewers: ['Reviewer 1'] });
      onSaved(data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: Z + 1 }}>
      <DialogTitle>{editing ? 'Edit the review details' : 'Add reviews received'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField label="Title *" value={form.title} onChange={set('title')} fullWidth size="small"
                placeholder={journal || 'e.g. Evaluation Summary Report'}
                helperText={journal && !form.title.startsWith(journal) ? (
                  <Box component="span">
                    Target journal: {journal}.{' '}
                    <Box component="span" sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => setForm((f) => ({ ...f, title: journalTitle(journal, existingTitles.filter((t) => t !== round?.title)) }))}>
                      Use it as the title
                    </Box>
                  </Box>
                ) : undefined} />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField label="From" select value={form.source} onChange={set('source')} fullWidth size="small" SelectProps={MENU}>
                {SOURCES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Received" type="date" value={form.received_on} onChange={set('received_on')} fullWidth
                size="small" slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Response due" type="date" value={form.response_due} onChange={set('response_due')}
                fullWidth size="small" slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Decision" select value={form.decision} onChange={set('decision')} fullWidth size="small" SelectProps={MENU}>
                {DECISIONS.map((o) => <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField label="Overall score" type="number" value={form.overall_score} onChange={set('overall_score')} fullWidth size="small" />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField label="Out of" type="number" value={form.max_score} onChange={set('max_score')} fullWidth size="small" />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField label="Threshold" type="number" value={form.threshold} onChange={set('threshold')} fullWidth size="small" />
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Scores per criterion</Typography>
            <Stack spacing={1}>
              {form.criteria.map((c, i) => (
                <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <TextField label="Criterion" value={c.name || ''} onChange={(e) => setCriterion(i, 'name', e.target.value)} size="small" sx={{ flex: 2 }} />
                  <TextField label="Score" type="number" value={c.score ?? ''} onChange={(e) => setCriterion(i, 'score', e.target.value)} size="small" sx={{ width: 90 }} />
                  <TextField label="Out of" type="number" value={c.max ?? ''} onChange={(e) => setCriterion(i, 'max', e.target.value)} size="small" sx={{ width: 90 }} />
                  <TextField label="Threshold" type="number" value={c.threshold ?? ''} onChange={(e) => setCriterion(i, 'threshold', e.target.value)} size="small" sx={{ width: 100 }} />
                  <TextField label="Comment" value={c.comment || ''} onChange={(e) => setCriterion(i, 'comment', e.target.value)} size="small" sx={{ flex: 3 }} />
                  <IconButton size="small" aria-label="Remove criterion"
                    onClick={() => setForm((f) => ({ ...f, criteria: f.criteria.filter((_, j) => j !== i) }))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                onClick={() => setForm((f) => ({ ...f, criteria: [...f.criteria, { name: '', score: '', max: 5, threshold: '' }] }))}>
                Add criterion
              </Button>
            </Stack>
          </Box>

          <TextField label="Summary from the panel or the editor" value={form.summary} onChange={set('summary')}
            fullWidth multiline minRows={3} size="small" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── one reviewer comment and our answer ──────────────────────────────────────
function PointCard({ point, members, sections, onSaved, onDelete, dragHandleProps }) {
  const [draft, setDraft] = useState(point);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setDraft(point); }, [point]);

  const save = async (changes) => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axiosService.patch(`/proposal/review-points/${point.id}/`, changes);
      onSaved(data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };
  // text saves when you leave the field; choices save at once
  const blurSave = (k) => () => { if ((draft[k] || '') !== (point[k] || '')) save({ [k]: draft[k] }); };
  const pick = (k) => (e) => {
    const value = e.target.value || null;
    setDraft((d) => ({ ...d, [k]: value }));
    save({ [k]: value });
  };
  const status = STATUSES.find((s) => s.value === draft.status) || STATUSES[0];

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid',
      borderLeftColor: draft.kind === 'major' ? 'warning.main' : draft.kind === 'minor' ? 'info.light' : 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {dragHandleProps && (
          <Tooltip title="Drag to move this comment up or down">
            <Box {...dragHandleProps} aria-label="Move this comment"
              sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab', ml: -0.75, '&:hover': { color: 'text.secondary' } }}>
              <DragIcon fontSize="small" />
            </Box>
          </Tooltip>
        )}
        <Typography variant="subtitle2" fontWeight={700}>Comment {point.number}</Typography>
        <TextField select size="small" value={draft.kind} onChange={pick('kind')} variant="standard" SelectProps={MENU}
          sx={{ minWidth: 80 }}>
          {KINDS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <Chip size="small" label={status.label} color={status.color} />
        {saving && <CircularProgress size={14} />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Delete this comment">
          <IconButton size="small" onClick={() => onDelete(point)} aria-label="Delete comment"><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      <TextField value={draft.comment || ''} onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
        onBlur={blurSave('comment')} fullWidth multiline minRows={2} size="small" label="The reviewer wrote"
        sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'action.hover' } }} />
      <TextField value={draft.response || ''} fullWidth multiline minRows={3} size="small" label="Our response"
        onChange={(e) => setDraft((d) => ({ ...d, response: e.target.value }))}
        onBlur={() => {
          if ((draft.response || '') === (point.response || '')) return;
          const moveOn = point.status === 'open' && (draft.response || '').trim();
          save({ response: draft.response, ...(moveOn && { status: 'drafted' }) });
        }} />

      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField select size="small" fullWidth label="Status" value={draft.status} onChange={pick('status')} SelectProps={MENU}>
            {STATUSES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField select size="small" fullWidth label="Who answers" value={draft.assignee || ''} onChange={pick('assignee')} SelectProps={MENU}>
            <MenuItem value="">Nobody yet</MenuItem>
            {members.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField select size="small" fullWidth label="Section changed" value={draft.section || ''} onChange={pick('section')} SelectProps={MENU}>
            <MenuItem value="">None</MenuItem>
            {sections.map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField size="small" fullWidth label="Where it changed" value={draft.change_note || ''}
            placeholder="e.g. 2.1, second paragraph"
            onChange={(e) => setDraft((d) => ({ ...d, change_note: e.target.value }))} onBlur={blurSave('change_note')} />
        </Grid>
      </Grid>
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Paper>
  );
}

// ── the response letter ──────────────────────────────────────────────────────
function markdownToDocx(markdown, title) {
  const children = [];
  const runs = (text) => text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean).map((part) => (
    part.startsWith('**') ? new TextRun({ text: part.slice(2, -2), bold: true })
      : part.startsWith('*') ? new TextRun({ text: part.slice(1, -1), italics: true }) : new TextRun(part)));
  markdown.split('\n').forEach((line) => {
    if (line.startsWith('# ')) children.push(new Paragraph({ text: line.slice(2), heading: HeadingLevel.HEADING_1 }));
    else if (line.startsWith('## ')) children.push(new Paragraph({ text: line.slice(3), heading: HeadingLevel.HEADING_2 }));
    else if (line.startsWith('>')) children.push(new Paragraph({ children: [new TextRun({ text: line.replace(/^>\s?/, ''), italics: true, color: '555555' })], indent: { left: 400 } }));
    else if (line.trim()) children.push(new Paragraph({ children: runs(line), spacing: { after: 120 } }));
  });
  return new Document({ title, sections: [{ children }] });
}

function LetterDialog({ open, onClose, round }) {
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!open || !round) return;
    setMarkdown('');
    setError(null);
    axiosService.get(`/proposal/review-rounds/${round.id}/letter/`)
      .then(({ data }) => setMarkdown(data.markdown))
      .catch((err) => setError(apiErrorMessage(err, 'Could not build the letter.')));
  }, [open, round]);

  const download = async () => {
    const blob = await Packer.toBlob(markdownToDocx(markdown, `Response to the reviewers: ${round.title}`));
    saveAs(blob, `Response to reviewers - ${round.title}.docx`.replace(/[\\/:*?"<>|]/g, '-'));
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(markdown); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { setError('Copy did not work: select the text and copy it by hand.'); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: Z + 1 }}>
      <DialogTitle>Response to the reviewers</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!markdown && !error ? <CircularProgress size={24} /> : (
          // the letter as it will read: headings, quoted comments, bold "Response." (Copy still gives the markdown)
          <Box sx={{ fontSize: '0.95rem', lineHeight: 1.6,
            '& h1': { fontSize: '1.4rem', fontWeight: 700, mt: 0, mb: 1.5 },
            '& h2': { fontSize: '1.15rem', fontWeight: 700, mt: 3, mb: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider' },
            '& p': { mt: 0, mb: 1.25 },
            '& blockquote': { m: 0, mb: 1.25, pl: 2, py: 0.5, borderLeft: 3, borderColor: 'secondary.light',
              color: 'text.secondary', fontStyle: 'italic', '& p': { mb: 0.5 } } }}>
            <LabMarkdown>{markdown}</LabMarkdown>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<CopyIcon />} onClick={copy} disabled={!markdown}>{copied ? 'Copied' : 'Copy text'}</Button>
        <Button startIcon={<DownloadIcon />} variant="contained" onClick={download} disabled={!markdown}>Download Word</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── the dialog ───────────────────────────────────────────────────────────────
export default function ReviewsDialog({ open, onClose, proposal, isPublication }) {
  const [rounds, setRounds] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedReviewer, setSelectedReviewer] = useState(null);   // a reviewer's page, or null: the round
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editRound, setEditRound] = useState(null);
  const [letterOpen, setLetterOpen] = useState(false);
  const [paste, setPaste] = useState({ text: '', kind: 'other' });
  const [pasting, setPasting] = useState(false);
  const [layout, setLayout] = useState(readLayout);
  const sidebarRef = useRef(null);
  const updateLayout = (changes) => setLayout((prev) => { const next = { ...prev, ...changes }; saveLayout(next); return next; });

  // drag the bar between the reviewers and the to-dos: the reviewers' share of the height, 15–85%
  const startResize = (e) => {
    e.preventDefault();
    const box = sidebarRef.current?.getBoundingClientRect();
    if (!box) return;
    const move = (ev) => {
      const y = ev.clientY - box.top;
      setLayout((prev) => ({ ...prev, split: Math.min(85, Math.max(15, (100 * y) / box.height)) }));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      document.body.style.cursor = '';
      setLayout((prev) => { saveLayout(prev); return prev; });
    };
    document.body.style.cursor = 'row-resize';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  const members = proposal?.consortium_users || [];
  const sections = useMemo(() => {
    const flat = [];
    (proposal?.sections || []).forEach((s) => {
      flat.push({ id: s.id, title: s.title });
      (s.subsections || []).forEach((sub) => flat.push({ id: sub.id, title: `  ${sub.title}` }));
    });
    return flat;
  }, [proposal?.sections]);

  const load = useCallback(() => {
    if (!proposal?.id) return;
    setLoading(true);
    axiosService.get('/proposal/review-rounds/', { params: { proposal: proposal.id } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setRounds(list);
        setSelectedId((id) => {
          if (list.some((r) => r.id === id)) return id;
          setSelectedReviewer(null);
          return list[0]?.id || null;
        });
      })
      .catch((err) => setError(apiErrorMessage(err, 'Could not load the reviews.')))
      .finally(() => setLoading(false));
  }, [proposal?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const round = rounds.find((r) => r.id === selectedId);
  const replaceRound = (r) => setRounds((prev) => (prev.some((x) => x.id === r.id) ? prev.map((x) => (x.id === r.id ? r : x)) : [r, ...prev]));
  const replacePoint = (p) => setRounds((prev) => prev.map((r) => {
    if (r.id !== p.round) return r;
    const points = r.points.map((x) => (x.id === p.id ? p : x));
    const answered = (x) => ['addressed', 'rebutted'].includes(x.status);
    const done = points.filter(answered).length;
    // the sidebar's counts follow as answers are saved
    const reviewer_pages = (r.reviewer_pages || []).map((pg) => ({
      ...pg, done: points.filter((x) => x.reviewer === pg.name && answered(x)).length }));
    return { ...r, points, reviewer_pages,
      progress: { total: points.length, done, open: points.filter((x) => x.status === 'open').length } };
  }));

  const pasteReview = async () => {
    setPasting(true);
    setError(null);
    try {
      const { data } = await axiosService.post(`/proposal/review-rounds/${round.id}/paste/`, { ...paste, reviewer: selectedReviewer });
      replaceRound(data);
      setPaste((p) => ({ ...p, text: '' }));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add the comments.'));
    } finally {
      setPasting(false);
    }
  };
  const deletePoint = async (point) => {
    if (!window.confirm(`Delete comment ${point.number} of ${point.reviewer}?`)) return;
    try {
      await axiosService.delete(`/proposal/review-points/${point.id}/`);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete it.'));
    }
  };
  const open_ = (roundId, reviewer = null) => { setSelectedId(roundId); setSelectedReviewer(reviewer); };
  const pageNames = (r) => (r.reviewer_pages || []).map((pg) => pg.name);
  // a new reviewer page: "Reviewer 3" after two
  const addReviewer = async (r) => {
    const names = pageNames(r);
    let n = names.length + 1;
    while (names.includes(`Reviewer ${n}`)) n += 1;
    try {
      const { data } = await axiosService.patch(`/proposal/review-rounds/${r.id}/`, { reviewers: [...names, `Reviewer ${n}`] });
      replaceRound(data);
      open_(r.id, `Reviewer ${n}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add the reviewer.'));
    }
  };
  const setRecommendation = async (r, name, value) => {
    try {
      const { data } = await axiosService.patch(`/proposal/review-rounds/${r.id}/`,
        { recommendations: { ...(r.recommendations || {}), [name]: value } });
      replaceRound(data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the recommendation.'));
    }
  };
  // one reviewer's page with their comments, answers and recommendation; the round and the others stay
  const removeReviewer = async (r, name) => {
    const count = (r.points || []).filter((x) => x.reviewer === name).length;
    if (count && !window.confirm(`Delete ${name}'s page with their ${count} comment${count === 1 ? '' : 's'} and your answers? The other reviewers stay.`)) return;
    try {
      const { data } = await axiosService.post(`/proposal/review-rounds/${r.id}/remove-reviewer/`, { reviewer: name });
      replaceRound(data);
      open_(r.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove the reviewer.'));
    }
  };

  const deleteRound = async () => {
    if (!window.confirm(`Delete "${round.title}" and all its comments and answers?`)) return;
    try {
      await axiosService.delete(`/proposal/review-rounds/${round.id}/`);
      open_(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete it.'));
    }
  };

  const visiblePoints = (round?.points || []).filter((p) => !selectedReviewer || p.reviewer === selectedReviewer);
  // a comment dropped elsewhere among the same reviewer's: renumbered at once, saved, then the server's order
  const moveComment = async ({ source, destination }) => {
    if (!destination || source.droppableId !== destination.droppableId || source.index === destination.index) return;
    const reviewer = source.droppableId;
    const theirs = round.points.filter((x) => (x.reviewer || 'Reviewer') === reviewer);
    const moved = [...theirs];
    const [item] = moved.splice(source.index, 1);
    moved.splice(destination.index, 0, item);
    const renumbered = new Map(moved.map((x, i) => [x.id, { ...x, number: i + 1 }]));
    const others = round.points.filter((x) => !renumbered.has(x.id));
    const at = round.points.findIndex((x) => renumbered.has(x.id));
    const points = [...others.slice(0, at), ...renumbered.values(), ...others.slice(at)];
    setRounds((prev) => prev.map((r) => (r.id === round.id ? { ...r, points } : r)));
    try {
      const { data } = await axiosService.post(`/proposal/review-rounds/${round.id}/reorder/`,
        { reviewer, ids: moved.map((x) => x.id) });
      replaceRound(data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not move the comment.'));
      load();
    }
  };

  const byReviewer = useMemo(() => {
    const groups = new Map();
    (round?.points || []).filter((p) => !selectedReviewer || p.reviewer === selectedReviewer).forEach((p) => {
      const key = p.reviewer || 'Reviewer';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return [...groups.entries()];
  }, [round?.points, selectedReviewer]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen sx={{ zIndex: Z }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReviewIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          Reviews and responses
          <Typography variant="body2" color="text.secondary">
            {proposal?.acronym || proposal?.title}
            {isPublication && proposal?.target_journal && <> · submitted to <b>{proposal.target_journal}</b></>}
            : the reviews this {isPublication ? 'publication' : 'proposal'} received, and the team's answer to each comment
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 0 }}>
        {/* the sidebar: the reviewers above, the revision's to-dos below, each scrolling on its own; the bar
            between them drags, and the sidebar collapses to a strip */}
        {layout.collapsed ? (
          <Box sx={{ width: 40, flexShrink: 0, borderRight: 1, borderColor: 'divider', pt: 1, display: 'flex', justifyContent: 'center' }}>
            <Tooltip title="Show reviewers and to-dos" placement="right">
              <IconButton size="small" onClick={() => updateLayout({ collapsed: false })} aria-label="Show the sidebar">
                <ExpandIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
        <Box ref={sidebarRef} sx={{ width: { xs: 220, md: 300 }, flexShrink: 0, borderRight: 1, borderColor: 'divider',
          display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: round ? `0 0 ${layout.split}%` : '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Hide the sidebar">
              <IconButton size="small" onClick={() => updateLayout({ collapsed: true })} aria-label="Hide the sidebar">
                <CollapseIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} sx={{ flex: 1, minWidth: 0 }}
              onClick={() => { setEditRound(null); setFormOpen(true); }}>
              Add reviews received
            </Button>
          </Box>
          {loading && <LinearProgress />}
          {/* each round, and under it its reviewers' pages */}
          <List dense>
            {rounds.map((r) => (
              <React.Fragment key={r.id}>
                <ListItemButton selected={r.id === selectedId && !selectedReviewer} onClick={() => open_(r.id)} sx={{ pr: 6 }}>
                  <ListItemText
                    primary={r.title}
                    slotProps={{ primary: { fontWeight: 600 } }}
                    secondary={`${r.decision ? `${label(DECISIONS, r.decision)} · ` : ''}${r.progress.done}/${r.progress.total} answered`}
                  />
                  <Tooltip title="Add a reviewer's comments">
                    <IconButton size="small" edge="end" aria-label={`Add a reviewer to ${r.title}`}
                      onClick={(e) => { e.stopPropagation(); addReviewer(r); }}
                      sx={{ position: 'absolute', right: 8 }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemButton>
                {(r.reviewer_pages || []).map((pg) => (
                  <ListItemButton key={pg.name} sx={{ pl: 4 }} selected={r.id === selectedId && selectedReviewer === pg.name}
                    onClick={() => open_(r.id, pg.name)}>
                    <ListItemText primary={pg.name}
                      secondary={[pg.recommendation && label(RECOMMENDATIONS, pg.recommendation),
                        pg.total ? `${pg.done}/${pg.total} answered` : 'No comments yet'].filter(Boolean).join(' · ')} />
                  </ListItemButton>
                ))}
              </React.Fragment>
            ))}
          </List>
          {!loading && rounds.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              No reviews yet. Add the evaluation report or the reviewers' comments when they arrive.
            </Typography>
          )}
        </Box>

          {/* the bar between the two: drag to share the height */}
          {round && (
            <Box onPointerDown={startResize} role="separator" aria-orientation="horizontal" aria-label="Resize reviewers and to-dos"
              sx={{ height: 8, flexShrink: 0, cursor: 'row-resize', borderTop: 1, borderBottom: 1, borderColor: 'divider',
                bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none',
                '&:hover': { bgcolor: 'action.selected' } }}>
              <Box sx={{ width: 32, height: 3, borderRadius: 2, bgcolor: 'text.disabled' }} />
            </Box>
          )}

          {/* the selected round's revision to-dos: assigned to members (who get a notification), on its own board */}
          {round && (
            <Box sx={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', p: 1.5, bgcolor: 'background.default' }}>
              <ProposalTodoPanel
                proposalId={proposal?.id}
                reviewRoundId={round.id}
                sections={proposal?.sections || []}
                consortiumMembers={proposal?.consortium_users || []}
                boardName={`Revision: ${round.title}`}
                title="Revision to-dos"
                emptyText="Split the revision into tasks and assign them to the team."
                zIndex={Z + 1}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                For {round.title}, on its board “Revision: {round.title}”. Whoever is assigned gets a notification.
              </Typography>
            </Box>
          )}
        </Box>
        )}

        {/* the selected round */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {!round ? (
            <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
              <ReviewIcon sx={{ fontSize: 56, opacity: 0.25, mb: 1 }} />
              <Typography>Choose reviews on the left, or add the ones you received.</Typography>
            </Box>
          ) : (
            <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="h5" fontWeight={700}>
                    {selectedReviewer ? (
                      <>
                        <Box component="span" sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                          onClick={() => open_(round.id)}>{round.title}</Box>
                        {' › '}{selectedReviewer}
                      </>
                    ) : round.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
                    <Chip size="small" variant="outlined" label={label(SOURCES, round.source)} />
                    {isPublication && round.source === 'journal' && proposal?.target_journal && !round.title.startsWith(proposal.target_journal) && (
                      <Chip size="small" color="secondary" variant="outlined" label={proposal.target_journal} />
                    )}
                    {round.decision && <Chip size="small" color={DECISION_COLOR[round.decision] || 'default'} label={label(DECISIONS, round.decision)} />}
                    {round.received_on && <Typography variant="body2" color="text.secondary">Received {fmtDate(round.received_on)}</Typography>}
                    {round.response_due && <Typography variant="body2" color="warning.main" fontWeight={600}>· Response due {fmtDate(round.response_due)}</Typography>}
                  </Box>
                </Box>
                <Button startIcon={<LetterIcon />} variant="contained" onClick={() => setLetterOpen(true)} disabled={!round.points.length}>
                  Response letter
                </Button>
                <Button startIcon={<EditIcon />} onClick={() => { setEditRound(round); setFormOpen(true); }}>Edit details</Button>
                {/* on a reviewer's page: that reviewer only; on the journal's page: the whole round */}
                <Tooltip title={selectedReviewer ? `Delete ${selectedReviewer} and their comments` : 'Delete this round with all its reviewers'}>
                  <IconButton aria-label={selectedReviewer ? `Delete ${selectedReviewer}` : 'Delete these reviews'}
                    onClick={() => (selectedReviewer ? removeReviewer(round, selectedReviewer) : deleteRound())}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* the reviewer's recommendation, on their page */}
              {selectedReviewer && (
                <TextField select size="small" label={`${selectedReviewer} recommends`} sx={{ maxWidth: 320 }}
                  value={(round.recommendations || {})[selectedReviewer] || ''} SelectProps={MENU}
                  onChange={(e) => setRecommendation(round, selectedReviewer, e.target.value)}>
                  <MenuItem value="">Not given</MenuItem>
                  {RECOMMENDATIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              )}

              {/* scores */}
              {(round.overall_score != null || round.criteria?.length > 0) && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  {round.overall_score != null && (
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Overall: {Number(round.overall_score)}{round.max_score != null ? ` / ${Number(round.max_score)}` : ''}
                      {round.threshold != null && (
                        <Typography component="span" variant="body2" color={Number(round.overall_score) >= Number(round.threshold) ? 'success.main' : 'error.main'} sx={{ ml: 1 }}>
                          (threshold {Number(round.threshold)})
                        </Typography>
                      )}
                    </Typography>
                  )}
                  <Stack spacing={0.75}>
                    {(round.criteria || []).map((c, i) => {
                      const below = c.threshold != null && c.score != null && Number(c.score) < Number(c.threshold);
                      return (
                        <Box key={i}>
                          <Typography variant="body2">
                            <b>{c.name}</b>{c.score != null && c.score !== '' ? `: ${c.score}${c.max ? ` / ${c.max}` : ''}` : ''}
                            {below && <Typography component="span" variant="caption" color="error.main"> below threshold</Typography>}
                          </Typography>
                          {c.comment && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{c.comment}</Typography>}
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              )}
              {round.summary && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Summary from the panel or editor</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{round.summary}</Typography>
                </Paper>
              )}

              {/* progress */}
              {visiblePoints.length > 0 && (() => {
                // the round's progress, or the reviewer's on their page
                const done = visiblePoints.filter((x) => ['addressed', 'rebutted'].includes(x.status)).length;
                const todo = visiblePoints.filter((x) => x.status === 'open').length;
                return (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {done} of {visiblePoints.length} comments answered · {todo} to do
                    </Typography>
                    <LinearProgress variant="determinate" value={(100 * done) / visiblePoints.length} sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                );
              })()}

              {/* paste a review: on a reviewer's page, for that reviewer */}
              {selectedReviewer && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Add {selectedReviewer}'s comments</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Paste the whole review. Markdown headings (## Methods) split it first, then numbered or bulleted items, otherwise each paragraph becomes a comment.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                  <TextField size="small" select label="Comments are" value={paste.kind} SelectProps={MENU}
                    onChange={(e) => setPaste((p) => ({ ...p, kind: e.target.value }))} sx={{ minWidth: 140 }}>
                    {KINDS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Stack>
                <TextField fullWidth multiline minRows={4} size="small" placeholder="Paste the review here…"
                  value={paste.text} onChange={(e) => setPaste((p) => ({ ...p, text: e.target.value }))}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }} />
                <Button startIcon={<PasteIcon />} variant="outlined" sx={{ mt: 1 }} onClick={pasteReview}
                  disabled={pasting || !paste.text.trim()}>
                  {pasting ? 'Splitting…' : 'Split to comments'}
                </Button>
                {visiblePoints.length === 0 && (
                  <Button size="small" color="inherit" sx={{ mt: 1, ml: 1, color: 'text.secondary' }}
                    onClick={() => removeReviewer(round, selectedReviewer)}>
                    Remove this reviewer
                  </Button>
                )}
              </Paper>
              )}
              {!selectedReviewer && (round.reviewer_pages || []).length === 0 && (
                <Alert severity="info">
                  Add the reviewers' comments with the + next to the round on the left: one page per reviewer.
                </Alert>
              )}

              {/* the comments, by reviewer: each reviewer's can be dragged into a new order */}
              <DragDropContext onDragEnd={moveComment}>
              {byReviewer.map(([reviewer, points]) => (
                <Box key={reviewer}>
                  {!selectedReviewer && (
                    <Divider textAlign="left" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={() => open_(round.id, reviewer)}>
                        {reviewer}
                        {(round.recommendations || {})[reviewer] && (
                          <Chip size="small" sx={{ ml: 1 }} color={DECISION_COLOR[(round.recommendations || {})[reviewer]] || 'default'}
                            label={label(RECOMMENDATIONS, (round.recommendations || {})[reviewer])} />
                        )}
                      </Typography>
                    </Divider>
                  )}
                  {/* drag a comment up or down among this reviewer's; the numbers follow */}
                  <Droppable droppableId={reviewer}>
                    {(drop) => (
                      <Box ref={drop.innerRef} {...drop.droppableProps}>
                        {points.map((p, index) => (
                          <Draggable key={p.id} draggableId={String(p.id)} index={index}>
                            {(drag, snapshot) => (
                              <Box ref={drag.innerRef} {...drag.draggableProps}
                                sx={{ mb: 1.5, ...(snapshot.isDragging && { boxShadow: 6, borderRadius: 2 }) }}
                                style={drag.draggableProps.style}>
                                <PointCard point={p} members={members} sections={sections}
                                  onSaved={replacePoint} onDelete={deletePoint} dragHandleProps={drag.dragHandleProps} />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {drop.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              ))}
              </DragDropContext>
            </Stack>
          )}
        </Box>
      </DialogContent>

      <RoundForm open={formOpen} onClose={() => setFormOpen(false)} round={editRound} proposalId={proposal?.id}
        isPublication={isPublication} targetJournal={proposal?.target_journal}
        existingTitles={rounds.map((r) => r.title)}
        onSaved={(r) => { replaceRound(r); if (r.id !== selectedId) open_(r.id, (r.reviewer_pages || [])[0]?.name || null); }} />
      <LetterDialog open={letterOpen} onClose={() => setLetterOpen(false)} round={round} />
    </Dialog>
  );
}
